/**
 * Web Components Registration
 * This file registers custom web components using Shadow DOM
 * for encapsulated, reusable UI elements.
 */

// PiTech Loading Spinner Web Component
class PiTechSpinner extends HTMLElement {
  private shadow: ShadowRoot;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    const size = this.getAttribute("size") || "40";
    const color = this.getAttribute("color") || "currentColor";

    this.shadow.innerHTML = `
      <style>
        :host {
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .spinner {
          width: ${size}px;
          height: ${size}px;
          border: 3px solid transparent;
          border-top-color: ${color};
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      </style>
      <div class="spinner" part="spinner"></div>
    `;
  }

  static get observedAttributes() {
    return ["size", "color"];
  }

  attributeChangedCallback() {
    this.connectedCallback();
  }
}

// PiTech Badge Web Component with Shadow DOM
class PiTechBadge extends HTMLElement {
  private shadow: ShadowRoot;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    const variant = this.getAttribute("variant") || "default";
    const colors: Record<string, { bg: string; text: string }> = {
      default: { bg: "hsl(0 0% 96%)", text: "hsl(0 0% 9%)" },
      success: { bg: "hsl(142 76% 95%)", text: "hsl(142 76% 36%)" },
      warning: { bg: "hsl(38 92% 95%)", text: "hsl(38 92% 40%)" },
      danger: { bg: "hsl(0 84% 95%)", text: "hsl(0 84% 60%)" },
      info: { bg: "hsl(217 91% 95%)", text: "hsl(217 91% 60%)" },
    };
    const { bg, text } = colors[variant] || colors.default;

    this.shadow.innerHTML = `
      <style>
        :host {
          display: inline-flex;
        }
        .badge {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.75rem;
          font-size: 0.75rem;
          font-weight: 500;
          border-radius: 9999px;
          background: ${bg};
          color: ${text};
          white-space: nowrap;
        }
      </style>
      <span class="badge" part="badge"><slot></slot></span>
    `;
  }

  static get observedAttributes() {
    return ["variant"];
  }

  attributeChangedCallback() {
    this.connectedCallback();
  }
}

// PiTech Card Web Component
class PiTechCard extends HTMLElement {
  private shadow: ShadowRoot;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    const elevated = this.hasAttribute("elevated");

    this.shadow.innerHTML = `
      <style>
        :host {
          display: block;
        }
        .card {
          background: var(--card-bg, hsl(0 0% 100%));
          border: 1px solid var(--card-border, hsl(0 0% 90%));
          border-radius: 0.75rem;
          padding: 1.5rem;
          ${elevated ? "box-shadow: 0 4px 20px -4px rgba(0,0,0,0.1);" : ""}
          transition: box-shadow 0.2s ease, transform 0.2s ease;
        }
        :host([interactive]) .card:hover {
          box-shadow: 0 8px 30px -8px rgba(0,0,0,0.15);
          transform: translateY(-2px);
        }
        @media (prefers-color-scheme: dark) {
          .card {
            --card-bg: hsl(0 0% 9%);
            --card-border: hsl(0 0% 15%);
          }
        }
      </style>
      <div class="card" part="card"><slot></slot></div>
    `;
  }
}

// Register all custom elements
export function registerWebComponents() {
  if (!customElements.get("pitech-spinner")) {
    customElements.define("pitech-spinner", PiTechSpinner);
  }
  if (!customElements.get("pitech-badge")) {
    customElements.define("pitech-badge", PiTechBadge);
  }
  if (!customElements.get("pitech-card")) {
    customElements.define("pitech-card", PiTechCard);
  }
}

// Check if Web Components are supported
export function webComponentsSupported(): boolean {
  return (
    "customElements" in window &&
    "attachShadow" in Element.prototype
  );
}

export default registerWebComponents;
